import { toZonedTime, format } from 'date-fns-tz';
import { shiftedNowMs } from './timeOverride';

/**
 * Calculates the duration between two dates in days
 * 
 * @param startDate - The start date
 * @param endDate - The end date
 * @param includeWeekends - Whether to include weekends in calculation (default: false)
 * @returns Number of days between the two dates
 * 
 * @example
 * // Exclude weekends (default)
 * calculateDurationInDays(new Date('2024-01-01'), new Date('2024-01-05')); // Returns 3 (if Jan 1-5 includes 1 weekend day)
 * 
 * @example
 * // Include weekends
 * calculateDurationInDays(new Date('2024-01-01'), new Date('2024-01-05'), true); // Returns 4
 */
export const calculateDurationInDays = (
    startDate: Date, 
    endDate: Date, 
    includeWeekends: boolean = false
): number => {
    // A day in milliseconds (1000ms * 60s * 60m * 24h)
    const oneDay: number = 1000 * 60 * 60 * 24;

    // Convert both dates to UTC timestamps to ensure consistent calculations
    const startUTC: number = Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
    );
    const endUTC: number = Date.UTC(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
    );

    // Calculate the difference in milliseconds
    const timeDifferenceMs: number = Math.abs(endUTC - startUTC);
    
    // Convert to days
    const totalDays: number = Math.round(timeDifferenceMs / oneDay);

    // If including weekends, return total days
    if (includeWeekends) {
        return totalDays;
    }

    // Otherwise, count only weekdays (default behavior)
    return countWeekdays(startDate, endDate);
};

/**
 * Counts the number of weekdays (Monday-Friday) between two dates
 * 
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Number of weekdays between the dates (inclusive)
 */
export const countWeekdays = (startDate: Date, endDate: Date): number => {
    // Create copies of the dates to avoid mutating the originals
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Reset time portion to ensure accurate day counting
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    let weekdays = 0;
    const currentDate = new Date(start);
    
    // Loop through each day and count weekdays
    while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        
        // Check if it's a weekday (Monday = 1, Tuesday = 2, Wednesday = 3, Thursday = 4, Friday = 5)
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
            weekdays++;
        }
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return weekdays;
};

/**
 * Alternative version that accepts string dates
 * 
 * @param startDateStr - Start date as string
 * @param endDateStr - End date as string
 * @param includeWeekends - Whether to include weekends in calculation (default: false)
 * @returns Number of days between the two dates
 */
export const calculateDurationInDaysFromStrings = (
    startDateStr: string, 
    endDateStr: string,
    includeWeekends: boolean = false
): number => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    return calculateDurationInDays(startDate, endDate, includeWeekends);
};

/**
 * Calculates duration including weekends (convenience function)
 * 
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Number of days including weekends
 */
export const calculateDurationIncludingWeekends = (startDate: Date, endDate: Date): number => {
    return calculateDurationInDays(startDate, endDate, true);
};

/**
 * Calculates duration excluding weekends (convenience function)
 * 
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Number of weekdays only
 */
export const calculateDurationExcludingWeekends = (startDate: Date, endDate: Date): number => {
    return calculateDurationInDays(startDate, endDate, false);
};

/**
 * Gets the number of weekend days between two dates
 * 
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Number of weekend days between the dates
 */
export const countWeekendDays = (startDate: Date, endDate: Date): number => {
    const totalDays = calculateDurationIncludingWeekends(startDate, endDate);
    const weekdays = countWeekdays(startDate, endDate);
    return totalDays - weekdays;
};

/**
 * Checks if a date is a weekend day
 * 
 * @param date - The date to check
 * @returns True if the date is Saturday or Sunday
 */
export const isWeekend = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
};

/**
 * Checks if a date is a weekday
 * 
 * @param date - The date to check
 * @returns True if the date is Monday-Friday
 */
export const isWeekday = (date: Date): boolean => {
    return !isWeekend(date);
};

/**
 * Adds business days to a date (skipping weekends)
 * 
 * @param date - Starting date
 * @param days - Number of business days to add
 * @returns New date after adding business days
 */
export const addBusinessDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    let remainingDays = days;
    
    while (remainingDays > 0) {
        result.setDate(result.getDate() + 1);
        if (isWeekday(result)) {
            remainingDays--;
        }
    }
    
    return result;
};

export const convertUTCToKL = (utcDateString: string, dateTimeFormat = 'yyyy-MM-dd HH:mm:ss', timeZone = 'Asia/Kuala_Lumpur'): string => {

  // Ensure the string is parsed as UTC by appending 'Z' if no timezone indicator exists
  const utcString = utcDateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(utcDateString)
    ? utcDateString
    : utcDateString + 'Z';
  const klDate = toZonedTime(utcString, timeZone);

  return format(klDate, dateTimeFormat, { timeZone });
};

/**
 * Display a UTC datetime from the API as a Kuala Lumpur date, e.g. "21 May 2026".
 *
 * Prefer this over `new Date(x).toLocaleDateString(...)`:
 *  - It pins the zone to Asia/Kuala_Lumpur instead of trusting the browser clock.
 *  - It accepts BOTH API formats — ISO-8601 (with Z/offset) and the
 *    timezone-naive `Y-m-d H:i:s` strings the Assessment/Evaluation endpoints
 *    return — because convertUTCToKL appends the missing 'Z'.
 *
 * Returns `fallback` for null/empty/invalid input.
 */
export const formatKLDate = (utcDateString?: string | null, fallback = '-'): string => {
  if (!utcDateString) return fallback;
  try {
    return convertUTCToKL(utcDateString, 'dd MMM yyyy');
  } catch {
    return fallback;
  }
};

/**
 * Display a UTC datetime from the API as a KL date + time,
 * e.g. "21 May 2026, 02:30 PM". See {@link formatKLDate} for format handling.
 */
export const formatKLDateTime = (utcDateString?: string | null, fallback = '-'): string => {
  if (!utcDateString) return fallback;
  try {
    return convertUTCToKL(utcDateString, 'dd MMM yyyy, hh:mm a');
  } catch {
    return fallback;
  }
};

/**
 * Display a UTC datetime from the API as a KL time only,
 * e.g. "02:30 PM". See {@link formatKLDate} for format handling.
 */
export const formatKLTime = (utcDateString?: string | null, fallback = '-'): string => {
  if (!utcDateString) return fallback;
  try {
    return convertUTCToKL(utcDateString, 'hh:mm a');
  } catch {
    return fallback;
  }
};

/**
 * True when a UTC datetime from the API is at or before "now".
 *
 * Accepts BOTH API formats — ISO-8601 (with Z/offset) and the timezone-naive
 * `Y-m-d H:i:s` strings some endpoints return — by appending 'Z' when no zone
 * marker is present, so a naive string is not misread as the viewer's local
 * time. The comparison itself is an absolute-instant comparison, so the result
 * is correct regardless of the browser's timezone.
 *
 * Returns `false` for null/empty/invalid input.
 */
export const isUTCDateInPast = (utcDateString?: string | null): boolean => {
  if (!utcDateString) return false;
  const hasZone = utcDateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(utcDateString);
  const normalized = hasZone ? utcDateString : utcDateString.replace(' ', 'T') + 'Z';
  const time = new Date(normalized).getTime();
  return !Number.isNaN(time) && shiftedNowMs() >= time;
};